import React, { useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Paper,
    TextField,
    Typography,
    Divider,
    CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddOtherPartyDialog from "../../Components/Dialogs/AddOtherPartyDialog";

const INVOICE_TYPE_LABEL = {
    "1": "Invoice with Tax",
    "2": "Invoice without Tax",
    "3": "Personal Sale",
};

const CustomerSelect = () => {
    const authHeader = useAuthHeader();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const invoiceType = searchParams.get("invoiceType");

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isRefresh, setIsRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddCustomerDialogOpen, setIsAddCustomerDialogOpen] = useState(false);

    useEffect(() => {
        const getCustomers = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${REACT_APP_BACKEND_URL}/otherparty`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                });
                const data = await response.json();
                setCustomers(
                    data.otherParties.map((c) => ({ label: c.name, value: c.id }))
                );
            } finally {
                setIsLoading(false);
            }
        };
        getCustomers();
    }, [authHeader, isRefresh]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "70vh",
                px: 3,
            }}
        >
            {isAddCustomerDialogOpen && (
                <AddOtherPartyDialog
                    isOpen={isAddCustomerDialogOpen}
                    onClose={() => {
                        setIsRefresh((r) => !r);
                        setIsAddCustomerDialogOpen(false);
                    }}
                    authHeader={authHeader}
                />
            )}

            <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: 480 }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        size="small"
                        onClick={() => navigate(-1)}
                        sx={{ mr: "auto" }}
                    >
                        Back
                    </Button>
                </Box>

                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                    Select Customer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sale type:{" "}
                    <strong>{INVOICE_TYPE_LABEL[invoiceType] ?? "Unknown"}</strong>
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Autocomplete
                        disablePortal
                        id="customer-select"
                        options={customers}
                        value={selectedCustomer}
                        onChange={(_, newValue) => {
                            if (newValue) setSelectedCustomer(newValue);
                        }}
                        getOptionLabel={(option) => option.label ?? ""}
                        renderInput={(params) => (
                            <TextField {...params} label="Customer" fullWidth />
                        )}
                    />
                )}

                <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => setIsAddCustomerDialogOpen(true)}
                >
                    Add New Customer
                </Button>

                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 2 }}
                    disabled={!selectedCustomer}
                    onClick={() =>
                        navigate(
                            `/AddInvoice?invoiceType=${invoiceType}&selectedCustomer=${selectedCustomer.value}`
                        )
                    }
                >
                    Continue →
                </Button>
            </Paper>
        </Box>
    );
};

export default CustomerSelect;
